/**
 * Resync Dialog Message Types
 * 
 * Defines the message protocol between the resync dialog and parent (commands.ts).
 * The dialog cannot directly call PowerPoint.run(), so it sends messages to the parent
 * which performs the operations and sends results back.
 */

import type { 
    ArtifactScanItem, 
    MissingFileInfo, 
    ScanProgress,
    SyncSummary,
    BatchUpdateResult,
} from './resync';

// ═══════════════════════════════════════════════════════════════════════════
// DIALOG → PARENT MESSAGES
// ═══════════════════════════════════════════════════════════════════════════

/** Messages sent from the dialog to the parent */
export type DialogToParentMessage = 
    | { action: 'ready' }
    | { action: 'scan' }
    | { action: 'rescanOne'; artifact: ArtifactScanItem }
    | { action: 'update'; artifacts: ArtifactScanItem[] }
    | { action: 'relocateFile'; excelId: string; newPath: string }
    | { action: 'close' };

// ═══════════════════════════════════════════════════════════════════════════
// PARENT → DIALOG MESSAGES
// ═══════════════════════════════════════════════════════════════════════════

/** Messages sent from the parent to the dialog */
export type ParentToDialogMessage =
    | { action: 'init' }
    | { action: 'scanProgress'; progress: ScanProgress }
    | { action: 'scanComplete'; artifacts: ArtifactScanItem[]; missingFiles: MissingFileInfo[]; summary: SyncSummary }
    | { action: 'rescanOneComplete'; artifact: ArtifactScanItem }
    | { action: 'updateProgress'; current: number; total: number }
    | { action: 'updateComplete'; result: BatchUpdateResult }
    | { action: 'error'; message: string };

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Type-safe message sender for dialog → parent
 */
export function sendToParent(message: DialogToParentMessage): void {
    Office.context.ui.messageParent(JSON.stringify(message));
}

/**
 * Type-safe message sender for parent → dialog
 */
export function sendToDialog(dialog: Office.Dialog, message: ParentToDialogMessage): void {
    console.log(`[sendToDialog] Sending message: ${message.action}`);
    try {
        dialog.messageChild(JSON.stringify(message));
        console.log(`[sendToDialog] Message sent successfully: ${message.action}`);
    } catch (error) {
        console.error(`[sendToDialog] Error sending message ${message.action}:`, error);
    }
}

/**
 * Parse a message from parent (used in dialog)
 */
export function parseParentMessage(messageString: string): ParentToDialogMessage | null {
    try {
        return JSON.parse(messageString) as ParentToDialogMessage;
    } catch {
        console.error('[ResyncDialog] Failed to parse parent message:', messageString);
        return null;
    }
}

/**
 * Parse a message from dialog (used in parent/commands)
 */
export function parseDialogMessage(messageString: string): DialogToParentMessage | null {
    try {
        return JSON.parse(messageString) as DialogToParentMessage;
    } catch {
        console.error('[Commands] Failed to parse dialog message:', messageString);
        return null;
    }
}

